import { useEffect, useState } from 'react';
import { assistantSuggestionsForVenue } from './assistantSuggestedQuestions';

export const ASSISTANT_DESKTOP_MIN_PX = 1024;

/** true cuando viewport >= minWidth (1024 = Tailwind lg, coherente con tablet en la app) */
export function useMediaMinWidth(minWidth) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(`(min-width: ${minWidth}px)`).matches;
  });

  useEffect(() => {
    const mq = window.matchMedia(`(min-width: ${minWidth}px)`);
    const onChange = () => setMatches(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [minWidth]);

  return matches;
}

export function conversationRecencyTimestamp(c) {
  if (!c || typeof c !== 'object') return 0;
  const u = Date.parse(c.updated_at || '');
  const cr = Date.parse(c.created_at || '');
  if (!Number.isNaN(u)) return u;
  if (!Number.isNaN(cr)) return cr;
  return 0;
}

export function pickRandomAssistantSuggestion(excludeId = null, venueType = null) {
  const pool = assistantSuggestionsForVenue(venueType);
  if (!pool.length) return null;
  if (pool.length === 1) return pool[0];
  let pick = pool[Math.floor(Math.random() * pool.length)];
  let guard = 0;
  while (pick.id === excludeId && guard < 16) {
    pick = pool[Math.floor(Math.random() * pool.length)];
    guard += 1;
  }
  return pick;
}

export function formatRelativeTime(isoOrMs) {
  const ts = typeof isoOrMs === 'number' ? isoOrMs : Date.parse(isoOrMs || '');
  if (Number.isNaN(ts)) return 'Ahora';
  const diffMs = Date.now() - ts;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `Hace ${diffH} h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return 'Ayer';
  if (diffD < 7) return `Hace ${diffD} días`;
  return new Date(ts).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
}

/**
 * Agrupa conversaciones para el sidebar (HOY, AYER, ESTA SEMANA, ANTERIORES).
 */
export function groupConversationsByPeriod(conversations) {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfYesterday = startOfToday - 86400000;
  const startOfWeek = startOfToday - 6 * 86400000;

  const groups = {
    today: { label: 'Hoy', items: [] },
    yesterday: { label: 'Ayer', items: [] },
    thisWeek: { label: 'Esta semana', items: [] },
    older: { label: 'Anteriores', items: [] },
  };

  for (const c of conversations || []) {
    const t = conversationRecencyTimestamp(c);
    if (t >= startOfToday) groups.today.items.push(c);
    else if (t >= startOfYesterday) groups.yesterday.items.push(c);
    else if (t >= startOfWeek) groups.thisWeek.items.push(c);
    else groups.older.items.push(c);
  }

  return [groups.today, groups.yesterday, groups.thisWeek, groups.older].filter((g) => g.items.length > 0);
}

export function countChatMessages(messages) {
  return (messages || []).filter((m) => m.role === 'user' || m.role === 'assistant').length;
}

/** Mensaje desde API/JSONB (incluye reasoning_content para modelos thinking). */
export function mapStoredChatMessage(m) {
  if (!m || typeof m !== 'object') return { role: 'assistant', content: '' };
  const entry = { role: m.role, content: m.content ?? '' };
  if (m.reasoning_content) entry.reasoning_content = m.reasoning_content;
  return entry;
}

/** Entrada de historial para POST /ai/chat(/stream). */
export function messageToApiHistoryEntry(m) {
  const entry = { role: m.role, content: m.content };
  if (m.reasoning_content) entry.reasoning_content = m.reasoning_content;
  return entry;
}
